import React, {useContext, useEffect, useState} from 'react'
import {GridActionsCellItem} from '@mui/x-data-grid'
import {KTSVG} from '../../../_metronic/helpers'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'
import DataGridComponent from '../../components/DataGrid'
import ImportCollectionModal from '../../components/ImportCollectionModal'
import {CHANNELS} from '../../constants/collections'
import {ProductsContext} from '../../context'
import config from '../../../config.json'
import ChannelDetailModal from './ChannelDetailModal'

const localizedValues = () => ({fr: '', en: '', es: '', it: ''})

const editableChannel = channel => {
  const contactForm = channel.contact_form
  if(contactForm?.recipients && contactForm.primary_recipient === undefined && contactForm.secondary_recipients === undefined) {
    const [primaryRecipient = '', ...secondaryRecipients] = contactForm.recipients
    return {
      ...channel,
      _persisted: true,
      contact_form: {...contactForm, primary_recipient: primaryRecipient, secondary_recipients: secondaryRecipients},
    }
  }
  return {...channel, _persisted: true}
}

const emptyChannel = () => ({
  id: '',
  enabled: false,
  is_default: false,
  promo_codes_enabled: false,
  brand: {
    display_name: '',
    legal_publisher: '',
    copyright: '',
    primary_color: '',
    background_color: '',
    logo_alt: '',
  },
  urls: {
    public_domain: '',
    api_base_url: '',
    functions_base_url: '',
    upload_path: '/upload',
    privacy_path: '/privacy',
    password_reset_path: '/reset-password',
    basket_path: '/basket',
    checkout_success_path: '/checkout-success',
  },
  email: {
    sender_name: '',
    from_address: '',
    reply_to: null,
    mailgun_domain: '',
    template: '',
  },
  email_assets: {
    apple_wallet_button: localizedValues(),
    google_wallet_button: localizedValues(),
  },
  wallet: {
    enabled: false,
    apple: {
      pass_type_identifier: '',
      logo_url: '',
      icon_url: '',
      foreground_color: '',
      background_color: '',
      label_color: '',
    },
    google: {
      issuer_id: '',
      class_id: '',
      hero_image_url: '',
      logo_url: '',
      background_color: '',
    },
  },
  documents: {
    PURCHASE_SUMMARY: localizedValues(),
    PURCHASE_ORDER: localizedValues(),
    INVOICE: localizedValues(),
    PURCHASE_SUMMARY_ANCAV: localizedValues(),
    INVOICE_ANCAV: localizedValues(),
  },
  localized_copy_namespace: '',
})

const ChannelsPage = () => {
  const {
    channels,
    channelsLoaded,
    fetchChannels,
    createChannel,
    updateChannel,
    deleteChannel,
  } = useContext(ProductsContext)
  const [currentChannel, setCurrentChannel] = useState(null)
  const [saving, setSaving] = useState(false)
  const [importCollectionModalOpen, setImportCollectionModalOpen] = useState(false)

  useEffect(() => {
    if(!channelsLoaded) fetchChannels()
  }, [channelsLoaded, fetchChannels])

  const onDetailSubmit = async () => {
    const data = {...currentChannel}
    const id = data.id?.trim().toLowerCase()
    if(!id || !/^[a-z0-9][a-z0-9_-]*$/.test(id)) {
      alert('Channel ID is required and may only contain lowercase letters, numbers, underscores, and hyphens.')
      return
    }
    if(!data.brand?.display_name?.trim()) {
      alert('Display name is required.')
      return
    }
    if(!data._persisted && channels.some(channel => channel.id === id)) {
      alert(`A channel with ID "${id}" already exists.`)
      return
    }

    if(data.contact_form) {
      const primaryRecipient = data.contact_form.primary_recipient?.trim() || ''
      const secondaryRecipients = [...new Set((data.contact_form.secondary_recipients || [])
        .flatMap(value => value.split(','))
        .map(value => value.trim())
        .filter(Boolean))]
      if([primaryRecipient, ...secondaryRecipients].filter(Boolean)
        .some(value => !/^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(value))) {
        alert('Enter a valid email address for each contact form recipient.')
        return
      }
      data.contact_form = {
        ...data.contact_form,
        primary_recipient: primaryRecipient,
        secondary_recipients: secondaryRecipients.filter(value => value.toLowerCase() !== primaryRecipient.toLowerCase()),
      }
      delete data.contact_form.recipients
    }

    delete data._persisted
    delete data.id
    data.email = {
      ...(data.email || {}),
      reply_to: data.email?.reply_to?.trim() || null,
    }

    setSaving(true)
    const response = currentChannel._persisted
      ? await updateChannel(id, data)
      : await createChannel(id, data)
    setSaving(false)
    if(response?.error) {
      alert(response.error.message || response.error)
      return
    }
    setCurrentChannel(null)
  }

  const onDetailDelete = async () => {
    const label = currentChannel?.brand?.display_name || currentChannel?.id
    if(!confirm(`Delete channel "${label}"? This can break purchases, emails, and wallet passes that reference it.`)) return
    setSaving(true)
    const response = await deleteChannel(currentChannel.id)
    setSaving(false)
    if(response?.error) {
      alert(response.error.message || response.error)
      return
    }
    setCurrentChannel(null)
  }

  const rows = channels.map(channel => ({
    ...channel,
    display_name: channel.brand?.display_name || 'Missing display name',
    public_domain: channel.urls?.public_domain || '',
    status: channel.enabled === false ? 'Disabled' : 'Enabled',
    promo_codes: channel.promo_codes_enabled ? 'Enabled' : 'Disabled',
  }))

  return (
    <>
      <ToolbarCustom
        pageTitle='Channels'
        primaryButton={channelsLoaded && {onClick: () => setCurrentChannel(emptyChannel()), title: 'Add'}}
        loading={!channelsLoaded || saving}
        filterElements={[
          config.environment !== 'staging' && (
            <button className='btn btn-sm fw-bold btn-primary' onClick={() => setImportCollectionModalOpen(true)}>
              <KTSVG path='/media/icons/duotune/arrows/arr037.svg' className='svg-icon-3' />
              Import from staging
            </button>
          ),
        ]}
      />
      <div className='mt-3'>
        {channelsLoaded && (
          <DataGridComponent
            id='channels'
            rows={rows}
            columns={[
              {field: 'display_name', headerName: 'Channel', flex: 1},
              {field: 'id', headerName: 'ID', flex: 1},
              {field: 'public_domain', headerName: 'Public domain', flex: 1.5},
              {field: 'status', headerName: 'Status', width: 110},
              {field: 'promo_codes', headerName: 'Promo codes', width: 120},
              {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 80,
                getActions: ({id}) => [
                  <GridActionsCellItem
                    key={`edit-${id}`}
                    icon={<i className='bi bi-pencil-square text-primary'></i>}
                    label='Edit'
                    onClick={() => setCurrentChannel(editableChannel(channels.find(channel => channel.id === id)))}
                    color='inherit'
                  />,
                ],
              },
            ]}
            pageSizeOptions={[20, 50, 100]}
          />
        )}
      </div>
      <ChannelDetailModal
        show={Boolean(currentChannel)}
        handleClose={() => setCurrentChannel(null)}
        channel={currentChannel}
        setChannel={setCurrentChannel}
        onSubmit={onDetailSubmit}
        onDelete={onDetailDelete}
        saving={saving}
      />
      <ImportCollectionModal
        isOpen={importCollectionModalOpen}
        onClose={() => setImportCollectionModalOpen(false)}
        collectionName={CHANNELS}
        sourceEnvironment='staging'
        onImportComplete={fetchChannels}
      />
    </>
  )
}

export default ChannelsPage
